/**
 * The relationship map's graph: one node per real-world entity.
 *
 * Split from the renderer so the rules are testable on their own and the
 * component file exports components only.
 */
import type { DealPartyRoster } from '@/services/dealParties'
import {
  companyKeyOf,
  entityKeyOf,
  orgKeyOf,
  partyColor,
  partyName,
  partyRoles,
  personKeyOf,
} from './partyViewModel'

export type Node = {
  id: string
  label: string
  detail: string
  role: string
  /** Roles this entity holds on the deal, drawn on its edge from the deal. */
  roles: string[]
  color: string
  organization: boolean
  /** Connected to the deal itself (a roster entry), not only to a firm. */
  onDeal: boolean
  partyIds: string[]
  x: number
  y: number
}
export type Link = { from: string; to: string; title: string }

function sameText(a: string | null | undefined, b: string | null | undefined) {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase()
}

/** One node per real-world entity.
 *
 *  A roster entry is the entity it names — an organisation or a person, as
 *  the roster typed it — and its roles ride on its edge from the deal. A
 *  person recorded as an organisation's contact is their own node, linked to
 *  the organisation; a firm a person is recorded at is ONE node however many
 *  of its people are on the deal, and joins the roster entry for that firm
 *  when there is one. The live Reunion-shaped deal drew one sponsor as three
 *  nodes (two typed Person) and a principal as two (owner, 25 Sep). */
export function buildMapGraph(roster: DealPartyRoster): { nodes: Node[]; links: Link[] } {
  const nodes: Node[] = []
  const byKey = new Map<string, Node>()
  const links: Link[] = []
  // One edge per pair of entities. A firm's recorded contact who also has a
  // roster entry at that firm is joined by two statements; drawn twice, the
  // line doubled and two SVG children shared one key (Calloway Yards). The
  // edge keeps both statements in its title.
  const link = (from: string, to: string, title: string) => {
    const existing = links.find(
      (l) => (l.from === from && l.to === to) || (l.from === to && l.to === from),
    )
    if (!existing) {
      links.push({ from, to, title })
    } else if (!existing.title.split('; ').includes(title)) {
      existing.title = `${existing.title}; ${title}`
    }
  }
  const add = (key: string, make: () => Node, partyId?: string, roles: string[] = []) => {
    const existing = byKey.get(key)
    if (existing) {
      if (partyId && !existing.partyIds.includes(partyId)) existing.partyIds.push(partyId)
      for (const role of roles) if (!existing.roles.includes(role)) existing.roles.push(role)
      existing.role = existing.roles.join(' / ') || existing.role
      return existing
    }
    const node = make()
    byKey.set(key, node)
    nodes.push(node)
    return node
  }
  for (const party of roster.parties) {
    const roles = partyRoles(party)
    const organization = party.isEntity
    const key = entityKeyOf(party)
    const label = organization ? party.name : partyName(party)
    const node = add(
      key,
      () => ({
        id: key,
        label,
        detail: organization ? 'Organization' : party.title || 'Person',
        role: roles.join(' / '),
        roles: [...roles],
        color: partyColor(party),
        organization,
        onDeal: true,
        partyIds: [party.id],
        x: 0,
        y: 0,
      }),
      party.id,
      roles,
    )
    node.onDeal = true
    // An organisation's recorded contact person is a person, at that firm.
    // The firm's own record under another spelling ("EASTMERE HOLDINGS,
    // L.L.C." for "Eastmere Holdings LLC") is the firm, compared on its legal
    // name — never a second node typed Person.
    if (
      organization &&
      party.contact &&
      !sameText(party.contact, party.name) &&
      !sameText(party.contact, party.company) &&
      orgKeyOf(party.contact) !== orgKeyOf(party.name)
    ) {
      const person = add(
        personKeyOf(party.contact),
        () => ({
          id: personKeyOf(party.contact!),
          label: party.contact!,
          detail: party.title || 'Person',
          role: roles.join(' / '),
          roles: [],
          color: partyColor(party),
          organization: false,
          onDeal: false,
          partyIds: [party.id],
          x: 0,
          y: 0,
        }),
        party.id,
      )
      link(node.id, person.id, `${party.contact} · contact at ${party.name}`)
    }
    // A person's recorded firm: the roster entry for it, or one node for it.
    const companyKey = !organization ? companyKeyOf(party) : null
    if (companyKey && party.company) {
      const firm = add(companyKey, () => ({
        id: companyKey,
        label: party.company!,
        detail: 'Organization',
        role: 'Recorded affiliation',
        roles: [],
        color: partyColor(party),
        organization: true,
        onDeal: false,
        partyIds: [],
        x: 0,
        y: 0,
      }))
      link(
        firm.id,
        node.id,
        `${label} · at ${party.company}${party.companyBasis ? ` (${party.companyBasis})` : ''}`,
      )
    }
  }
  return { nodes, links }
}

